/* ------------------------------------------------------------------------- *
 * Street identity — one place that decides whether two street labels are the
 * SAME STREET.
 *
 * Why this exists: every street search in the app used to compare labels as
 * loose text, and text says "Pyrmont Bridge Road" contains "Bridge Road".
 * They are two different streets, two different areas, two different drivers
 * (Pyrmont Bridge Rd → BWAY chute 15 · Bridge Rd Glebe → a different round),
 * so a parcel matched that way is a misroute. The old rules failed three ways:
 *   · scan/label resolve used substring containment, so "Bridge Road" landed on
 *     "Pyrmont bridge rd";
 *   · the Driver Routes search asked only that every query word appear SOMEWHERE
 *     in the entry, so "Bridge Rd" returned Pyrmont Bridge Rd *and* Bridge St;
 *   · "Road" never matched a route list that writes "Rd", so the full, correct
 *     name — "Pyrmont Bridge Road" — found nothing at all.
 *
 * The rule here: a street is its NAME WORDS plus its TYPE. Both must agree.
 * An extra name word ("Pyrmont", "Little", "S(outh)") makes it a different
 * street; an extra QUALIFIER word ("Loading Bay", "Cnr", "Access Via") does
 * not, because the route lists carry those as prose around a real street.
 *
 * Pure + dependency-free → testable in Node (scripts/test-street-match.js).
 * ------------------------------------------------------------------------- */

/* Street-type words → one canonical type. Mirrors the route lists and AP's
   abbreviations, so "Road"/"RD"/"Rd." are all ROAD. CREST is this team's own
   shorthand for Crescent on the route lists. */
const TYPE_MAP = {
  ST: 'ST', STREET: 'ST', STR: 'ST', STS: 'ST', STREETS: 'ST',
  RD: 'RD', ROAD: 'RD', ROADS: 'RD',
  AV: 'AV', AVE: 'AV', AVENUE: 'AV', AVENUES: 'AV',
  LN: 'LN', LANE: 'LN', LANES: 'LN', LNS: 'LN',
  PL: 'PL', PLACE: 'PL', PLACES: 'PL',
  CR: 'CR', CRES: 'CR', CRESCENT: 'CR', CREST: 'CR',
  DR: 'DR', DRV: 'DR', DRIVE: 'DR',
  PDE: 'PDE', PARADE: 'PDE',
  TCE: 'TCE', TER: 'TCE', TERRACE: 'TCE',
  GDN: 'GDNS', GDNS: 'GDNS', GARDEN: 'GDNS', GARDENS: 'GDNS',
  CT: 'CT', COURT: 'CT',
  CL: 'CL', CLOSE: 'CL',
  SQ: 'SQ', SQUARE: 'SQ',
  CCT: 'CCT', CIRCUIT: 'CCT',
  CIR: 'CIR', CIRCLE: 'CIR',
  BVD: 'BVD', BLVD: 'BVD', BOULEVARD: 'BVD', BOULEVARDE: 'BVD',
  HWY: 'HWY', HIGHWAY: 'HWY',
  ESP: 'ESP', ESPLANADE: 'ESP',
  GR: 'GR', GRV: 'GR', GROVE: 'GR',
  WAY: 'WAY', QUAY: 'QY', QY: 'QY', QUAYS: 'QY',
  WALK: 'WALK', MALL: 'MALL', ROW: 'ROW', WHARF: 'WHARF',
  ARC: 'ARC', ARCADE: 'ARC',
};

/* Words that can sit around a street name on a route list without changing
   WHICH street it is — "Loading Bay Dawson Street" is Dawson Street, "Cnr
   Barcom St And Boundary Street" is Barcom St. A leading word that is NOT in
   here (Pyrmont, Little, S, New, Upper) makes it a different street. */
const QUALIFIERS = new Set([
  'LOADING', 'DOCK', 'BAY', 'BAYS', 'ACCESS', 'VIA', 'ENTRY', 'ENTRANCE', 'EXIT',
  'CNR', 'CRN', 'CORNER', 'OPP', 'OPPOSITE', 'REAR', 'FRONT', 'SIDE', 'BEHIND',
  'NEXT', 'NEAR', 'GATE', 'DOOR', 'LEVEL', 'LVL', 'L', 'FLOOR', 'SHOP', 'SUITE',
  'STE', 'SE', 'UNIT', 'U', 'APT', 'APARTMENT', 'FLAT', 'ROOM', 'RM', 'PO', 'GPO', 'POB',
  'LOT', 'TOWER', 'BUILDING', 'BLDG', 'CENTRE', 'CENTER', 'SHOPPING', 'PLAZA',
  'COMPLEX', 'CARPARK', 'PARKING', 'DELIVERY', 'DELIVERIES', 'WAREHOUSE', 'DEPOT',
  'ALL', 'ONLY', 'AND', 'THE', 'OF', 'AT', 'IN', 'ON', 'TO', 'FOR', 'GOING',
  'COLLECT', 'POST', 'PARCEL', 'MAILROOM', 'RECEPTION', 'CONCIERGE', 'TEAM',
  'EXCEPT', 'EXEPT', 'PLUS', 'INCL', 'INCLUDING', 'ETC', 'SIDES', 'BOTH', 'PART',
]);

const clean = (s) =>
  (s == null ? '' : String(s))
    .replace(/"[^"]*"/g, ' ')                    // quoted note on a route label
    /* Apostrophes and full stops CLOSE UP rather than split: "O'Connell" and
       "St. John's" have to land on the same words AP writes as OCONNELL and
       ST JOHNS, or a correctly-spelt list reads as a misspelling. */
    .replace(/['’.]/g, '')
    .replace(/[‘“”]/g, ' ')
    /* Unit/shop codes that lead with a letter — A005, T5, LG26, B1 — are not
       street numbers and leave a stray letter behind if stripped as numbers. */
    .replace(/\b[A-Za-z]{1,2}\d+[A-Za-z]?\b/g, ' ')
    .replace(/\d+[a-z]?\s*[.,]?\s*[-–]\s*\d+[a-z]?/gi, ' ') // RANGES: "1-207", "180.-234", "244-268A"
    .replace(/\d+[a-z]?/gi, ' ')                // any remaining house number ("400", "12A")
    .replace(/[^A-Za-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

/* Tokenise. Single letters are KEPT — "S Dowling St" is South Dowling Street,
   a different street from Dowling Street, and dropping the S merged the two. */
function tokens(label) {
  return clean(label).split(' ').filter(Boolean);
}

const isType = (t) => Object.prototype.hasOwnProperty.call(TYPE_MAP, t);

/**
 * Every street a label names, as { name:[words], type }.
 *
 * A route entry can name more than one ("Cnr Barcom St And Boundary Street"),
 * and can trail prose after the street ("Holt Street Only 2 Holt St going to
 * 14") — each street type word closes off the name words that precede it, so
 * the prose can never swallow the name. Index 0 is never a type, which keeps a
 * leading "St" as Saint ("St James Ave"). A label with no type word at all
 * ("Broadway", "The Corso") is one typeless street.
 */
export function streetsIn(label) {
  const toks = tokens(label);
  const out = [];
  let start = 0;
  for (let i = 1; i < toks.length; i++) {
    if (!isType(toks[i])) continue;
    /* A type word followed by another type word is part of the NAME: "Quay St",
       "Wharf Rd", "Grove St". Reading QUAY as the type there left the name as
       whatever came before it — "U 1513 2 Quay St" resolved to "U QY". */
    if (isType(toks[i + 1])) continue;
    const name = toks.slice(start, i);
    if (name.length) out.push({ name, type: TYPE_MAP[toks[i]] });
    start = i + 1;
  }
  if (!out.length && toks.length) {
    /* No type word at all. A trailing lone letter is a type that lost its tail on
       the route list ("Mary s" for Mary St, live on PAZMINOCM's round) — drop it
       and let the typeless name match any type, rather than reading the stray
       letter as part of the street's name. */
    const bare = toks.length > 1 && toks[toks.length - 1].length === 1 ? toks.slice(0, -1) : toks;
    out.push({ name: bare, type: null });
  }
  return out;
}

/** Canonical key for a street — "PYRMONT BRIDGE RD". Two labels share it only
 *  when they are the same street. Use for grouping/de-duping, not for matching
 *  (matching has to tolerate the prose — that's `matchStreet`). */
export function streetCanon(label) {
  const s = streetsIn(label)[0];
  if (!s) return '';
  return [...s.name, s.type].filter(Boolean).join(' ');
}

/* Markers that say the leading words are a VENUE, not part of the street name:
   "Broadway shopping centre Bay st" is Bay Street, "Xaver Building Ep Lab
   Victoria Street" is Victoria Street. One of these anywhere in the extra words
   makes the whole run prose. Without such a marker an extra word is a name word
   — which is what keeps Pyrmont Bridge Rd out of a Bridge Rd search. */
const VENUE = new Set([
  'LOADING', 'DOCK', 'ACCESS', 'VIA', 'CNR', 'CRN', 'CORNER', 'BUILDING', 'BLDG',
  'CENTRE', 'CENTER', 'SHOPPING', 'PLAZA', 'COMPLEX', 'TOWER', 'CARPARK',
  'WAREHOUSE', 'DEPOT', 'ENTRANCE', 'GATE', 'REAR', 'OPP', 'OPPOSITE', 'POB',
]);

/* Extra words around a matched name are acceptable when they are all prose, or
   when a venue marker declares the run to be a place rather than a street. */
const isProse = (words) => words.every((w) => QUALIFIERS.has(w)) || words.some((w) => VENUE.has(w));

/* Is `inner` a contiguous run inside `outer`? Returns the surrounding words. */
function containment(outer, inner) {
  if (inner.length > outer.length) return null;
  for (let i = 0; i + inner.length <= outer.length; i++) {
    let ok = true;
    for (let j = 0; j < inner.length; j++) if (outer[i + j] !== inner[j]) { ok = false; break; }
    if (ok) return { before: outer.slice(0, i), after: outer.slice(i + inner.length) };
  }
  return null;
}

/* Word-level prefix: every query word matches in order from position `at`, and
   the last one may still be half-typed ("common" → "COMMONWEALTH"). */
function isPrefix(name, q, at = 0) {
  if (q.length + at > name.length) return false;
  for (let i = 0; i < q.length; i++) {
    const last = i === q.length - 1;
    if (last ? !name[at + i].startsWith(q[i]) : name[at + i] !== q[i]) return false;
  }
  return true;
}

function compareOne(entry, query, allowPartial, browse) {
  if (entry.type && query.type && entry.type !== query.type) return null; // Bridge St ≠ Bridge Rd
  const a = entry.name, b = query.name;
  if (a.length === b.length && a.every((w, i) => w === b[i])) return 'exact';

  // One name sits inside the other: same street only if the extra words are
  // prose. "Loading Bay Dawson St" is Dawson St; "Pyrmont Bridge Rd" is NOT
  // Bridge Rd, and "S Dowling St" is not Dowling St.
  const inA = containment(a, b);
  if (inA && isProse([...inA.before, ...inA.after])) return 'qualified';
  const inB = containment(b, a);
  if (inB && isProse([...inB.before, ...inB.after])) return 'qualified';

  if (allowPartial && isPrefix(a, b)) return 'partial'; // still typing the name
  /* Browsing a list with a fragment and no type word ("bridge") is a different
     question from naming a street ("Bridge Rd") — it should show every street
     with that word in it, Pyrmont Bridge Rd included. Naming one stays strict,
     which is the whole point of this module. */
  if (browse && !query.type) {
    for (let at = 1; at < a.length; at++) if (isPrefix(a, b, at)) return 'partial';
  }
  return null;
}

const RANK = { exact: 3, qualified: 2, partial: 1 };

/**
 * How an entry on a route list relates to a searched/scanned street.
 *
 *   'exact'     same name words, same type — this IS the street
 *   'qualified' same street wrapped in route-list prose ("Loading Bay Dawson St")
 *   'partial'   the query is a half-typed prefix of it (typing search only)
 *   null        a different street
 *
 * Pass `{ allowPartial:true }` for a search box the user is typing into, and add
 * `{ browse:true }` for one that lists streets to pick from (a typeless fragment
 * then matches a word anywhere in the name). Leave both off wherever a wrong
 * answer sorts a parcel to the wrong driver.
 */
export function matchStreet(entryLabel, queryLabel, opts = {}) {
  const allowPartial = !!opts.allowPartial || !!opts.browse;
  const browse = !!opts.browse;
  const queries = streetsIn(queryLabel);
  const entries = streetsIn(entryLabel);
  if (!queries.length || !entries.length) return null;
  let best = null;
  for (const q of queries) {
    for (const e of entries) {
      const r = compareOne(e, q, allowPartial, browse);
      if (r && (!best || RANK[r] > RANK[best])) best = r;
    }
  }
  return best;
}

/** True when two labels name the same street (prose tolerated, typing not). */
export const sameStreet = (a, b) => matchStreet(a, b) !== null;

/* Levenshtein, capped — only ever used to SUGGEST a near miss, never to match.
   The route lists carry AP's and our own typos ("Bride rd" for Bridge Rd), and
   a sorter is better served by "did you mean Bride rd?" than by silence. */
function lev(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 99;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

/**
 * Is this entry a plausible MISSPELLING of the query street (same type, name
 * within two letters)? Never a match — a caller must show it as a question.
 */
export function looksLikeStreet(entryLabel, queryLabel) {
  const qs = streetsIn(queryLabel), es = streetsIn(entryLabel);
  for (const q of qs) {
    const qn = q.name.join(' ');
    if (qn.length < 4) continue;
    for (const e of es) {
      if (e.type && q.type && e.type !== q.type) continue;
      const en = e.name.join(' ');
      if (en.length < 4 || en === qn) continue;
      const d = lev(en, qn);
      if (d <= (Math.min(en.length, qn.length) >= 7 ? 2 : 1)) return true;
    }
  }
  return false;
}

/**
 * The street an ADDRESS is on, as a canonical key — "428 George St" and
 * "U 518 19 Hickson Rd" both reduce to the street alone. Takes the LAST street
 * named (an address ends on its street; a route entry may lead with one) and
 * drops the leading unit/venue words. Use for grouping addresses by street;
 * `matchStreet` is still what decides whether two labels are the same street.
 */
export function addressCanon(label) {
  const all = streetsIn(label);
  if (!all.length) return '';
  const s = all[all.length - 1];
  let name = [...s.name];
  /* Everything up to the LAST unit/venue word is where the parcel goes, not what
     the street is called: "Bank Card Team Level 6 140 Sussex Street" is Sussex
     Street, "Collect at Post 50-58 Macleay St" is Macleay St. */
  for (let i = name.length - 1; i >= 0; i--) {
    if (QUALIFIERS.has(name[i]) || VENUE.has(name[i])) {
      if (i < name.length - 1) name = name.slice(i + 1);
      break;
    }
  }
  while (name.length > 1 && (QUALIFIERS.has(name[0]) || VENUE.has(name[0]))) name.shift();
  return [...name, s.type].filter(Boolean).join(' ');
}

/** Human-readable street for display — "Pyrmont Bridge Rd" from a full entry. */
export function streetLabel(label) {
  return String(label == null ? '' : label)
    .replace(/(\d+)\s*[.,]?\s*[-–]\s*(\d+)/g, ' ')
    .replace(/[.,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
